import { NotApplicableTypes } from '../../constants/status';
import { VisibilityStatus } from '../../types/download';
import { ReportData } from '../../types/reportData';
import { TableColumnType } from '../../types/universalTracker';
import { getNaType } from '../../utils/universalTrackerValue';
import { defaultPrivateQuestionText } from './sgx-metric-report/common';

export function shouldDisplayPrivateText(value: { isPrivate?: boolean }, visibility: VisibilityStatus) {
  return value.isPrivate && visibility !== VisibilityStatus.Include;
}

export interface DataSource {
  loaded: boolean;
  width?: number;
  height?: number;
  chart?: string;
}

interface UtrvDataLookup {
  reportData: ReportData[];
  utrCode: string,
  tableColumn?: string
  fallback?: string | number;
  visibility: VisibilityStatus;
}

export interface UtrvDataResult {
  numericValue: number;
  value: string | number | undefined;
  isPrivate?: boolean;
  utrv?: ReportData
}

function getNumericValue(value: unknown, fallback: string | number | undefined): number {
  if (typeof value === 'number') {
    return value
  }
  return typeof fallback === 'number' ? fallback : 0;
}

export const getNaText = (naType: string) => {
  switch (naType) {
    case NotApplicableTypes.nr:
      return 'Not reporting';
    case NotApplicableTypes.na:
      return 'Not Applicable';
    default:
      return '-';
  }
}

export const getUtrvData = (data: UtrvDataLookup): UtrvDataResult => {

  const { visibility, reportData, utrCode, tableColumn, fallback } = data;

  const utrv = reportData.find(d => d.universalTracker.code === utrCode);
  if (!utrv) {
    return { numericValue: 0, value: fallback }
  }

  if (shouldDisplayPrivateText(utrv, visibility)) {
    return {
      numericValue: getNumericValue(undefined, fallback),
      value: defaultPrivateQuestionText,
      isPrivate: utrv.isPrivate,
      utrv,
    }
  }
  const naType = getNaType(utrv);
  if (naType) {
    return {
      numericValue: getNumericValue(undefined, fallback),
      value: getNaText(naType),
      utrv,
    }
  }

  if (tableColumn) {
    const value = utrv?.valueData?.table?.[0]?.find(col => col.code === tableColumn)?.value;
    if (Array.isArray(value)) {
      return {
        numericValue: 0,
        // Messed up, but we don't need this for now...
        value: value.join(', '),
        utrv,
      }
    }


    if (typeof value === 'string') {
      // Check if we need to convert to number
      const cols = utrv.universalTracker.valueValidation?.table?.columns ?? [];
      const column = cols.find(c => c.code === tableColumn)
      if (column && column.type === TableColumnType.Number) {
        try {
          const numericValue = parseFloat(value);
          return {
            numericValue: getNumericValue(numericValue, fallback),
            value: value,
            utrv,
          }
        } catch (e) {
          console.error(e)
        }
      }
    }


    return {
      numericValue: getNumericValue(value, fallback),
      value: value,
      utrv,
    }
  }

  if (utrv.valueType === 'text') {
    return {
      numericValue: getNumericValue(undefined, fallback),
      value: utrv.valueData?.data,
      utrv,
    }
  }

  return {
    numericValue: getNumericValue(utrv.value, fallback),
    value: utrv.value,
    utrv,
  }
}

export const sumUtrvDataResults = (values: UtrvDataResult[], visibility: VisibilityStatus): string | number => {

  let result = 0;

  for (const value of values) {
    if (shouldDisplayPrivateText(value, visibility)) {
      return defaultPrivateQuestionText;
    }

    result += value.numericValue
  }
  return result
}

export const getValueListName = (utrv: ReportData, valueListCode: string) => {
  const option = utrv.tableValueList?.map((valueList) => valueList?.options?.find(option => option.code === valueListCode));
  return option?.[0]?.name ?? valueListCode;
};
